import { ItemI, SectionItemI } from "../../shared";

/**
 * Получает правильное название номенклатуры для элемента в зависимости от используемого bitrixId
 * @param item - элемент секции
 * @param originalItem - исходный элемент с информацией о номенклатуре (опционально)
 * @returns название номенклатуры или исходное название
 */
export const getItemNomenclatureName = (item: SectionItemI, originalItem?: ItemI): string => {
    // Если нет данных о номенклатуре, возвращаем исходное имя
    if (!originalItem || (!originalItem.nameNomenkl && !originalItem.nameNomenklAlt)) {
        return item.name;
    }

    // Проверяем, используется ли альтернативный ID
    // Если bitrixId совпадает с bitrixIdAlt, значит используется альтернативный ID
    const isUsingAltId = item.bitrixIdAlt && item.bitrixId === item.bitrixIdAlt;
    
    // Если нет альтернативного ID, но текущий bitrixId совпадает с BITRIX_OFFER_ID_ALT из оригинального элемента
    const isUsingOriginalAltId = !isUsingAltId && originalItem.BITRIX_OFFER_ID_ALT && 
        item.bitrixId === originalItem.BITRIX_OFFER_ID_ALT.toString();
    
    if ((isUsingAltId || isUsingOriginalAltId) && originalItem.nameNomenklAlt) {
        // Используется альтернативный ID и есть альтернативное название
        return originalItem.nameNomenklAlt;
    } else if (!isUsingAltId && !isUsingOriginalAltId && originalItem.nameNomenkl) {
        // Используется основной ID и есть основное название
        return originalItem.nameNomenkl;
    }
    
    // Если подходящего названия номенклатуры нет, возвращаем исходное имя
    return item.name;
};

/**
 * Получает правильную цену для элемента в зависимости от используемого bitrixId
 * @param item - элемент секции
 * @param originalItem - исходный элемент с информацией о номенклатуре (опционально)
 * @returns цену или исходную цену
 */
export const getItemNomenclaturePrice = (item: SectionItemI, originalItem?: ItemI): number => {
    // Сначала пробуем использовать цены из номенклатуры
    if (originalItem && (originalItem.priceNomenkl !== undefined || originalItem.priceAltNomenkl !== undefined)) {
        // Проверяем, используется ли альтернативный ID
        const isUsingAltId = item.bitrixIdAlt && item.bitrixId === item.bitrixIdAlt;
        
        // Если нет альтернативного ID, но текущий bitrixId совпадает с BITRIX_OFFER_ID_ALT из оригинального элемента
        const isUsingOriginalAltId = !isUsingAltId && originalItem.BITRIX_OFFER_ID_ALT && 
            item.bitrixId === originalItem.BITRIX_OFFER_ID_ALT.toString();
        
        if ((isUsingAltId || isUsingOriginalAltId) && originalItem.priceAltNomenkl !== undefined) {
            // Используется альтернативный ID и есть альтернативная цена из номенклатуры
            return originalItem.priceAltNomenkl;
        } else if (!isUsingAltId && !isUsingOriginalAltId && originalItem.priceNomenkl !== undefined) {
            // Используется основной ID и есть основная цена из номенклатуры
            return originalItem.priceNomenkl;
        }
    }

    // Fallback на старую логику с priceOriginal и priceAlt
    if (!item.priceOriginal && !item.priceAlt) {
        return item.price;
    }

    // Проверяем, используется ли альтернативный ID
    const isUsingAltId = item.bitrixIdAlt && item.bitrixId === item.bitrixIdAlt;
    
    // Если нет альтернативного ID, но текущий bitrixId совпадает с BITRIX_OFFER_ID_ALT из оригинального элемента
    const isUsingOriginalAltId = !isUsingAltId && originalItem?.BITRIX_OFFER_ID_ALT && 
        item.bitrixId === originalItem.BITRIX_OFFER_ID_ALT.toString();
    
    if ((isUsingAltId || isUsingOriginalAltId) && item.priceAlt !== undefined) {
        // Используется альтернативный ID и есть альтернативная цена
        return item.priceAlt;
    } else if (!isUsingAltId && !isUsingOriginalAltId && item.priceOriginal !== undefined) {
        // Используется основной ID и есть основная цена
        return item.priceOriginal;
    }
    
    // Если подходящей цены нет, возвращаем исходную цену
    return item.price;
};

/**
 * Обновляет название элемента на основе используемого bitrixId
 * @param item - элемент секции для обновления
 * @param originalItem - исходный элемент с информацией о номенклатуре
 * @returns обновленный элемент с правильным названием
 */
export const updateItemNameWithNomenclature = (item: SectionItemI, originalItem?: ItemI): SectionItemI => {
    const nomenclatureName = getItemNomenclatureName(item, originalItem);
    
    return {
        ...item,
        name: nomenclatureName
    };
};

/**
 * Обновляет цену элемента на основе используемого bitrixId
 * @param item - элемент секции для обновления
 * @param originalItem - исходный элемент с информацией о номенклатуре
 * @returns обновленный элемент с правильной ценой
 */
export const updateItemPriceWithNomenclature = (item: SectionItemI, originalItem?: ItemI): SectionItemI => {
    const nomenclaturePrice = getItemNomenclaturePrice(item, originalItem);
    
    return {
        ...item,
        price: nomenclaturePrice
    };
};

/**
 * Обновляет название и цену элемента на основе используемого bitrixId
 * @param item - элемент секции для обновления
 * @param originalItem - исходный элемент с информацией о номенклатуре
 * @returns обновленный элемент с правильным названием и ценой
 */
export const updateItemNameAndPriceWithNomenclature = (item: SectionItemI, originalItem?: ItemI): SectionItemI => {
    const nomenclatureName = getItemNomenclatureName(item, originalItem);
    const nomenclaturePrice = getItemNomenclaturePrice(item, originalItem);
    
    return {
        ...item,
        name: nomenclatureName,
        price: nomenclaturePrice
    };
};

/**
 * Обновляет названия всех элементов в секциях на основе номенклатуры
 * @param sections - массив секций
 * @param itemsGroup - группы товаров с информацией о номенклатуре
 * @returns обновленные секции с правильными названиями
 */
export const updateAllItemNamesWithNomenclature = (
    sections: SectionItemI[][],
    itemsGroup: Array<{ ELEMENTS?: ItemI[] }>
): SectionItemI[][] => {
    const allItems = itemsGroup.flatMap(group => group.ELEMENTS || []);
    
    return sections.map(sectionItems =>
        sectionItems.map(item => {
            // Находим исходный элемент для получения названий номенклатуры
            const originalItem = allItems.find(original => 
                original.BITRIX_OFFER_ID === (item.bitrixIdOriginal || item.bitrixId) ||
                (original.BITRIX_OFFER_ID_ALT && 
                 (original.BITRIX_OFFER_ID_ALT === (item.bitrixIdOriginal || item.bitrixId) ||
                  original.BITRIX_OFFER_ID_ALT === item.bitrixIdAlt))
            );
            
            if (originalItem && (originalItem.nameNomenkl || originalItem.nameNomenklAlt)) {
                return updateItemNameWithNomenclature(item, originalItem);
            }
            
            return item;
        })
    );
};